// 📊 Dashboard SRI — Financiero / Gestión SRI
// KPIs y evolución de los comprobantes electrónicos recibidos del SRI
// (datos de financiero.documentos_recibidos_sri vía RPC sri_dashboard).
import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSort, SortableTh } from '@/features/financiero/tablaSort';
import {
  BarChart3,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Loader2,
  Percent,
  TrendingDown,
  TrendingUp,
  Wallet,
  ChevronDown,
  ChevronRight,
  Eye,
  ExternalLink,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/lib/api/client';
import { GLASS, GLASS_BG } from '@/lib/glass';
import { LOCALE } from '@/lib/utils/regional';

const FINANCIERO_HEADERS = {
  'Content-Profile': 'financiero',
  'Accept-Profile': 'financiero',
};

const MESES_CORTOS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const COLORES = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

interface Resumen {
  documentos: number;
  importe_total: number;
  iva: number;
  subtotal: number;
  promedio: number;
  proveedores: number;
}

interface DashboardData {
  anio: number;
  anios_disponibles: number[];
  resumen: Resumen;
  resumen_anterior: Resumen;
  por_mes: Array<{
    mes: number;
    docs_actual: number;
    importe_actual: number;
    docs_anterior: number;
    importe_anterior: number;
  }>;
  por_tipo: Array<{ tipo: string; documentos: number; importe: number }>;
  top_emisores: Array<{ ruc: string; razon_social: string | null; documentos: number; importe: number }>;
  procesamiento: {
    documentos: number;
    procesados: number;
    pendientes: number;
    importe_total: number;
    importe_procesado: number;
  };
  procesamiento_por_tipo: Array<{
    tipo: string;
    documentos: number;
    procesados: number;
    importe: number;
    importe_procesado: number;
  }>;
  emitidos: {
    resumen: { documentos: number; importe_total: number; iva: number; receptores: number };
    resumen_anterior: { documentos: number; importe_total: number; iva: number; receptores: number };
    por_mes: Array<{
      mes: number;
      docs_actual: number;
      importe_actual: number;
      docs_anterior: number;
      importe_anterior: number;
    }>;
    por_tipo: Array<{ tipo: string; documentos: number; importe: number }>;
    top_receptores: Array<{ ruc: string | null; razon_social: string | null; documentos: number; importe: number }>;
    procesamiento: Procesamiento;
    procesamiento_por_tipo: ProcesamientoTipo[];
  };
}

interface Procesamiento {
  documentos: number;
  procesados: number;
  pendientes: number;
  importe_total: number;
  importe_procesado: number;
}

interface ProcesamientoTipo {
  tipo: string;
  documentos: number;
  procesados: number;
  importe: number;
  importe_procesado: number;
}

// Tarjeta de avance de clasificación (Procesar Recibidos / Procesar Emitidos)
function AvanceProcesamiento({ titulo, p, porTipo }: {
  titulo: string;
  p: Procesamiento;
  porTipo: ProcesamientoTipo[];
}) {
  if (!p || p.documentos === 0) return null;
  const pct = Math.round((p.procesados / p.documentos) * 100);
  const completo = pct === 100;
  return (
    <Card className={GLASS}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-primary" />
            {titulo}
          </CardTitle>
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${
            completo
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
          }`}>
            {completo && <CheckCircle2 className="w-3 h-3" />}
            {pct}% completado
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-3 rounded-full transition-all ${completo ? 'bg-green-500' : 'bg-primary'}`}
              style={{ width: `${Math.max(pct, 1)}%` }}
            />
          </div>
          <div className="flex flex-wrap justify-between gap-2 mt-2 text-sm">
            <span>
              <span className="font-semibold text-green-600 dark:text-green-400">{p.procesados.toLocaleString(LOCALE)}</span> procesados ·{' '}
              <span className="font-semibold text-yellow-600 dark:text-yellow-400">{p.pendientes.toLocaleString(LOCALE)}</span> pendientes
              <span className="text-muted-foreground"> (de {p.documentos.toLocaleString(LOCALE)})</span>
            </span>
            <span className="text-muted-foreground">
              Importe clasificado: <span className="font-medium text-foreground">{usd(p.importe_procesado)}</span> de {usd(p.importe_total)}
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
          {porTipo.map((t) => {
            const tPct = t.documentos ? Math.round((t.procesados / t.documentos) * 100) : 0;
            return (
              <div key={t.tipo} className="text-sm">
                <div className="flex justify-between gap-2">
                  <span className="truncate" title={`Importe clasificado ${usd(t.importe_procesado)} de ${usd(t.importe)}`}>{t.tipo}</span>
                  <span className="whitespace-nowrap text-muted-foreground">
                    {t.procesados}/{t.documentos} · <span className="font-medium text-foreground">{tPct}%</span>
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full mt-1">
                  <div
                    className={`h-1.5 rounded-full ${tPct === 100 ? 'bg-green-500' : 'bg-primary'}`}
                    style={{ width: `${Math.max(tPct, 1)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

const usd = (v: number) =>
  v.toLocaleString(LOCALE, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

// Desglose por tipo de comprobante: donut + tabla de documentos e importes
function PorTipoCard({ porTipo, anio }: {
  porTipo: Array<{ tipo: string; documentos: number; importe: number }>;
  anio: number;
}) {
  const totalDocs = porTipo.reduce((s, t) => s + Number(t.documentos), 0);
  // Las notas de crédito RESTAN del importe total (neto = facturas − NC).
  const totalImporte = porTipo.reduce(
    (s, t) => s + (/nota de cr/i.test(t.tipo) ? -1 : 1) * Number(t.importe), 0);
  return (
    <Card className={GLASS}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Por tipo de comprobante</CardTitle>
      </CardHeader>
      <CardContent>
        {porTipo.length === 0 ? (
          <p className="text-sm text-muted-foreground py-10 text-center">Sin documentos en {anio}</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={170}>
              <PieChart>
                <Pie data={porTipo} dataKey="documentos" nameKey="tipo"
                  innerRadius={42} outerRadius={70} paddingAngle={2} label={(e) => e.documentos}>
                  {porTipo.map((_, i) => (
                    <Cell key={i} fill={COLORES[i % COLORES.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v, _n, item) =>
                    [`${v} docs · ${usd(Number((item.payload as any).importe))}`, (item.payload as any).tipo]
                  }
                />
              </PieChart>
            </ResponsiveContainer>
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1.5">Tipo</th>
                  <th className="py-1.5 text-right">Documentos</th>
                  <th className="py-1.5 text-right">Importe</th>
                </tr>
              </thead>
              <tbody>
                {porTipo.map((t, i) => (
                  <tr key={t.tipo} className="border-b last:border-0">
                    <td className="py-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                        style={{ backgroundColor: COLORES[i % COLORES.length] }} />
                      {t.tipo}
                    </td>
                    <td className="py-1.5 text-right">{Number(t.documentos).toLocaleString(LOCALE)}</td>
                    <td className="py-1.5 text-right whitespace-nowrap">{usd(Number(t.importe))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-1.5">Total</td>
                  <td className="py-1.5 text-right">{totalDocs.toLocaleString(LOCALE)}</td>
                  <td className="py-1.5 text-right whitespace-nowrap">{usd(totalImporte)}</td>
                </tr>
              </tfoot>
            </table>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Variación porcentual vs el año anterior; null si no hay base de comparación
function variacion(actual: number, anterior: number): number | null {
  if (!anterior) return null;
  return ((actual - anterior) / anterior) * 100;
}

// Abreviaturas para el desglose por tipo dentro de los cards
const TIPO_CORTO: Record<string, string> = {
  'Comprobante de Retención': 'Retención',
  'Liquidación de compra de bienes y prestación de servicios': 'Liquidación',
  'Liquidación de compra': 'Liquidación',
  'Nota de Crédito': 'Nota de Crédito',
  'Nota de Débito': 'Nota de Débito',
  'Guía de Remisión': 'Guía de Remisión',
};
const esNotaCredito = (tipo: string) => /nota.*cr.dito/i.test(tipo);

function KpiCard({
  titulo,
  valor,
  anterior,
  icono: Icono,
  formato = (v: number) => v.toLocaleString(LOCALE),
  desglose,
}: {
  titulo: string;
  valor: number;
  anterior: number;
  icono: typeof FileText;
  formato?: (v: number) => string;
  desglose?: Array<{ etiqueta: string; valor: number; informativo?: boolean }>;
}) {
  const delta = variacion(valor, anterior);
  return (
    <Card className={GLASS}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{titulo}</p>
          <Icono className="w-4 h-4 text-primary" />
        </div>
        <p className="text-2xl font-bold mt-1">{formato(valor)}</p>
        {delta !== null ? (
          <p
            className={`flex items-center gap-1 text-xs mt-1 ${
              delta >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}
          >
            {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(delta).toFixed(1)}% vs año anterior ({formato(anterior)})
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-1">Sin datos del año anterior</p>
        )}
        {desglose && desglose.length > 0 && (
          <div className="mt-2 pt-2 border-t space-y-0.5">
            {desglose.map((d) => (
              <p key={d.etiqueta} className="flex justify-between text-xs">
                <span className="text-muted-foreground truncate">{d.etiqueta}</span>
                <span className={`whitespace-nowrap ${
                  d.informativo ? 'text-muted-foreground'
                    : d.valor < 0 ? 'text-red-600 dark:text-red-400' : ''
                }`}>
                  {d.valor < 0 ? `− ${usd(Math.abs(d.valor))}` : usd(d.valor)}
                </span>
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Desglose de importes por tipo para el card: las NC restan (negativo) y las
// retenciones se muestran solo como referencia — no entran al total.
const desgloseImportes = (porTipo: Array<{ tipo: string; importe: number }>) =>
  porTipo
    .map((t) => {
      const esRetencion = /retenci/i.test(t.tipo);
      return {
        etiqueta: (TIPO_CORTO[t.tipo] || t.tipo) + (esRetencion ? ' (no suma)' : ''),
        valor: (esNotaCredito(t.tipo) ? -1 : 1) * Number(t.importe),
        informativo: esRetencion,
      };
    })
    .filter((d) => d.valor !== 0);

// Ruta física del RIDE → URL servida por Apache (/sri-docs).
const urlRide = (r: string | null | undefined) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

interface DocProyecto {
  lado: 'compra' | 'venta' | 'retencion'; id: string; fecha: string; tipo: string; serie: string | null; clave_acceso: string | null;
  identificacion: string | null; tercero: string | null; subtotal: number; iva: number; total: number; neto: number;
  estado: string | null; ride_url: string | null;
  // solo retenciones recibidas: factura(s) de venta que sustenta, su cliente y los proveedores del costo
  sustento?: string | null; sustento_cliente?: string | null; proveedores?: string | null;
}

const LADO: Record<DocProyecto['lado'], { label: string; cls: string }> = {
  compra: { label: 'Compra', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
  venta: { label: 'Venta', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  retencion: { label: 'Retención', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
};
const fechaDoc = (f: string) => new Date(f + 'T00:00:00').toLocaleDateString(LOCALE);
// Valor que se muestra en Total: la retención muestra lo RETENIDO; compras y ventas su neto (NC en negativo).
const valorDoc = (d: DocProyecto) => (d.lado === 'retencion' ? Number(d.total) : Number(d.neto));

type FiltroDoc = Record<'fecha' | 'lado' | 'tipo' | 'serie' | 'tercero' | 'subtotal' | 'iva' | 'total' | 'ride', string>;
const FILTRO_VACIO: FiltroDoc = { fecha: '', lado: '', tipo: '', serie: '', tercero: '', subtotal: '', iva: '', total: '', ride: '' };
const sinTildes = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const contiene = (valor: string, q: string) => !q.trim() || sinTildes(valor).includes(sinTildes(q.trim()));
// filtro numérico: «>100», «<50», «=30» o texto contenido («30,» / «1.8»)
const cumpleNumero = (n: number, q: string) => {
  const t = q.trim().replace(',', '.');
  if (!t) return true;
  const m = t.match(/^([<>]=?|=)\s*(-?\d+(?:\.\d+)?)$/);
  if (m) {
    const v = Number(m[2]);
    return m[1] === '>' ? n > v : m[1] === '>=' ? n >= v : m[1] === '<' ? n < v : m[1] === '<=' ? n <= v : Math.abs(n - v) < 0.005;
  }
  return n.toFixed(2).includes(t);
};

// Detalle de documentos de un proyecto (se abre al hacer clic en la fila) + visor del RIDE en línea.
function ProyectoDetalle({ proyectoId, anio, nombre }: { proyectoId: string | null; anio: number; nombre: string }) {
  const [rideVisto, setRideVisto] = useState<DocProyecto | null>(null);
  const [rideFalta, setRideFalta] = useState(false);
  const [f, setF] = useState<FiltroDoc>(FILTRO_VACIO);
  const { data, isLoading } = useQuery<DocProyecto[]>({
    queryKey: ['sriProyectoDetalle', proyectoId, anio],
    queryFn: async () =>
      (await apiClient.post('/rpc/sri_proyecto_detalle', { _proyecto_id: proyectoId, _anio: anio }, { headers: FINANCIERO_HEADERS })).data,
  });
  const todas = useMemo(() => data ?? [], [data]);
  const filas = useMemo(() => todas.filter((d) =>
    contiene(fechaDoc(d.fecha) + ' ' + d.fecha, f.fecha) &&
    (!f.lado || d.lado === f.lado) &&
    contiene(d.tipo + ' ' + (d.estado ?? ''), f.tipo) &&
    contiene(d.serie ?? '', f.serie) &&
    contiene([d.tercero, d.identificacion, d.sustento, d.sustento_cliente, d.proveedores].filter(Boolean).join(' '), f.tercero) &&
    cumpleNumero(Number(d.subtotal), f.subtotal) && cumpleNumero(Number(d.iva), f.iva) && cumpleNumero(valorDoc(d), f.total) &&
    (!f.ride || (f.ride === 'si' ? !!d.ride_url : !d.ride_url)),
  ), [todas, f]);
  const { sorted, sort, toggle } = useSort(filas);
  const hayFiltro = Object.values(f).some((v) => v.trim() !== '');
  // neto por documento: las notas de crédito restan; lo retenido va aparte (crédito tributario, no es gasto ni venta)
  const tot = filas.reduce((a, d) => ({
    gastos: a.gastos + (d.lado === 'compra' ? Number(d.neto) : 0),
    ventas: a.ventas + (d.lado === 'venta' ? Number(d.neto) : 0),
    retenido: a.retenido + (d.lado === 'retencion' ? Number(d.total) : 0),
  }), { gastos: 0, ventas: 0, retenido: 0 });
  const verRide = (d: DocProyecto) => {
    setRideVisto(d);
    setRideFalta(false);
    // si el PDF no está en este servidor, avisarlo en vez de mostrar la página de error
    fetch(urlRide(d.ride_url)!, { method: 'HEAD' })
      .then((r) => setRideFalta(!r.ok || !(r.headers.get('content-type') || '').includes('pdf')))
      .catch(() => setRideFalta(true));
    setTimeout(() => document.getElementById('ride-proyecto')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };
  const inp = 'h-7 w-full min-w-0 rounded border bg-background px-1.5 text-xs font-normal';
  const campo = (k: keyof FiltroDoc, ph: string, cls = '') => (
    <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} aria-label={`Filtrar ${k}`} className={`${inp} ${cls}`} />
  );

  return (
    <div className="space-y-3 rounded-lg border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">Documentos de «{nombre}» · {anio === 0 ? 'todos los años' : anio}</p>
        {hayFiltro && (
          <span className="text-xs text-muted-foreground">{filas.length} de {todas.length} ·{' '}
            <button type="button" className="text-primary hover:underline" onClick={() => setF(FILTRO_VACIO)}>Limpiar filtros</button></span>
        )}
      </div>
      {isLoading ? (
        <p className="py-4 text-center text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Cargando…</p>
      ) : todas.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">Este proyecto no tiene documentos en el período.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 pr-3">N°</th>
                <SortableTh col="fecha" label="Fecha" sort={sort} onSort={toggle} />
                <SortableTh col="lado" label="Clase" sort={sort} onSort={toggle} />
                <SortableTh col="tipo" label="Tipo" sort={sort} onSort={toggle} />
                <SortableTh col="serie" label="Número" sort={sort} onSort={toggle} />
                <SortableTh col="tercero" label="Proveedor / cliente" sort={sort} onSort={toggle} />
                <SortableTh col="subtotal" label="Subtotal / base" align="right" sort={sort} onSort={toggle} />
                <SortableTh col="iva" label="IVA" align="right" sort={sort} onSort={toggle} />
                <SortableTh col="total" label="Total / retenido" align="right" sort={sort} onSort={toggle} />
                <th className="py-2 text-center">RIDE</th>
              </tr>
              <tr className="border-b">
                <th />
                <th className="py-1 pr-2">{campo('fecha', 'dd/mm/aaaa', 'w-24')}</th>
                <th className="py-1 pr-2">
                  <select value={f.lado} onChange={(e) => setF({ ...f, lado: e.target.value })} aria-label="Filtrar clase" className={inp}>
                    <option value="">Todas</option><option value="compra">Compra</option><option value="venta">Venta</option><option value="retencion">Retención</option>
                  </select>
                </th>
                <th className="py-1 pr-2">{campo('tipo', 'Tipo')}</th>
                <th className="py-1 pr-2">{campo('serie', 'Número')}</th>
                <th className="py-1 pr-2">{campo('tercero', 'Nombre, RUC, factura…')}</th>
                <th className="py-1 pr-2">{campo('subtotal', '>100', 'text-right')}</th>
                <th className="py-1 pr-2">{campo('iva', '>0', 'text-right')}</th>
                <th className="py-1 pr-2">{campo('total', '=30', 'text-right')}</th>
                <th className="py-1">
                  <select value={f.ride} onChange={(e) => setF({ ...f, ride: e.target.value })} aria-label="Filtrar RIDE" className={inp}>
                    <option value="">Todos</option><option value="si">Con RIDE</option><option value="no">Sin RIDE</option>
                  </select>
                </th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 && (
                <tr><td colSpan={10} className="py-4 text-center text-muted-foreground">Ningún documento coincide con los filtros.</td></tr>
              )}
              {sorted.map((d, i) => (
                <tr key={d.lado + d.id} className={`border-b last:border-0 align-top ${rideVisto?.id === d.id ? 'bg-primary/10' : 'hover:bg-muted/40'}`}>
                  <td className="py-1.5 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{fechaDoc(d.fecha)}</td>
                  <td className="py-1.5 pr-3"><span className={`whitespace-nowrap rounded-full px-2 py-0.5 ${LADO[d.lado].cls}`}>{LADO[d.lado].label}</span></td>
                  <td className="py-1.5 pr-3">{d.tipo}{d.estado && d.estado !== 'autorizado' && <span className="ml-1 text-muted-foreground">({d.estado})</span>}</td>
                  <td className="py-1.5 pr-3 font-mono whitespace-nowrap">{d.serie ?? '—'}</td>
                  <td className="py-1.5 pr-3">
                    {d.lado === 'retencion' ? (
                      <div className="max-w-80 space-y-0.5">
                        <p className="truncate" title={d.proveedores ?? ''}>
                          <span className="text-muted-foreground">Proveedor: </span>
                          {d.proveedores ? <span className="font-medium">{d.proveedores}</span> : <span className="text-muted-foreground">— (la venta no tiene costos enlazados)</span>}
                        </p>
                        <p className="truncate" title={d.tercero ?? ''}><span className="text-muted-foreground">Agente de retención: </span>{d.tercero ?? '—'}{d.identificacion && <span className="text-muted-foreground"> · {d.identificacion}</span>}</p>
                        {d.sustento && <p className="truncate text-muted-foreground" title={`${d.sustento} · ${d.sustento_cliente ?? ''}`}>Sustenta: factura {d.sustento}{d.sustento_cliente ? ` · ${d.sustento_cliente}` : ''}</p>}
                      </div>
                    ) : (
                      <>
                        <p className="max-w-64 truncate" title={d.tercero ?? ''}>{d.tercero ?? '—'}</p>
                        {d.identificacion && <p className="text-muted-foreground">{d.identificacion}</p>}
                      </>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right whitespace-nowrap">{usd(Number(d.subtotal))}</td>
                  <td className="py-1.5 pr-3 text-right whitespace-nowrap">{usd(Number(d.iva))}</td>
                  <td className={`py-1.5 pr-3 text-right whitespace-nowrap font-medium ${d.lado === 'retencion' ? 'text-amber-700 dark:text-amber-400' : valorDoc(d) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{usd(valorDoc(d))}</td>
                  <td className="py-1.5 text-center">
                    {urlRide(d.ride_url) ? (
                      <button type="button" onClick={() => verRide(d)} title="Ver RIDE (PDF)" aria-label="Ver RIDE"
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-primary hover:bg-primary/10">
                        <Eye className="h-3.5 w-3.5" /> Ver</button>
                    ) : <span className="text-muted-foreground" title="RIDE no disponible">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold">
                <td className="py-2 pr-3" colSpan={6}>{filas.length} documento(s) · Gastos {usd(tot.gastos)} · Ventas {usd(tot.ventas)} · Retenido {usd(tot.retenido)}</td>
                <td className="py-2 pr-3" colSpan={2} />
                <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(tot.ventas - tot.gastos)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
          <p className="mt-1 text-[11px] text-muted-foreground">
            El total del pie es el neto (ventas − gastos); las notas de crédito restan. En las retenciones, «Subtotal / base» es la base
            retenida y «Total / retenido» el valor que le retuvieron: es crédito tributario a favor, por eso va aparte y no suma al neto.
            Filtros numéricos: escriba un valor o use &gt;, &lt; o = (p. ej. «&gt;100»).
          </p>
        </div>
      )}
      {rideVisto && urlRide(rideVisto.ride_url) && (
        <div id="ride-proyecto" className="scroll-mt-20 rounded-lg border p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">RIDE · {rideVisto.tipo} {rideVisto.serie} · {rideVisto.tercero}</p>
            <div className="flex items-center gap-3 text-xs">
              <a href={urlRide(rideVisto.ride_url)!} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                <ExternalLink className="h-3.5 w-3.5" /> Abrir en pestaña nueva</a>
              <button type="button" onClick={() => setRideVisto(null)} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                <X className="h-3.5 w-3.5" /> Cerrar</button>
            </div>
          </div>
          {rideFalta ? (
            <p className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              El archivo PDF de este RIDE no está en este servidor (el documento sí está registrado). Avise a soporte para recuperarlo.
            </p>
          ) : (
            <iframe title="RIDE" src={urlRide(rideVisto.ride_url)!} className="h-[75vh] w-full rounded border bg-white" />
          )}
        </div>
      )}
    </div>
  );
}

export function DashboardSri() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [seccion, setSeccion] = useState<'recibidos' | 'emitidos' | 'proyectos'>('recibidos');

  const [proyTodosAnios, setProyTodosAnios] = useState(false);
  const [proyOcultarVacios, setProyOcultarVacios] = useState(false);
  const [proyAbierto, setProyAbierto] = useState<string | null>(null);
  const { data: proyectos } = useQuery<{
    anio: number;
    proyectos: Array<{ proyecto_id: string | null; proyecto: string; identificacion: string | null; movimientos: number; gastos: number; ventas: number; neto: number }>;
    totales: { gastos: number; ventas: number; neto: number };
  }>({
    queryKey: ['sriProyectoResumen', proyTodosAnios ? 0 : anio],
    queryFn: async () =>
      (await apiClient.post('/rpc/sri_proyecto_resumen', { _anio: proyTodosAnios ? 0 : anio }, { headers: FINANCIERO_HEADERS })).data,
    enabled: seccion === 'proyectos',
  });
  // Filtro de la pestaña «Por proyecto»: un proyecto del desplegable y/o texto libre.
  const [proySel, setProySel] = useState('');
  const [proyTexto, setProyTexto] = useState('');
  const claveProy = (p: { proyecto: string; identificacion: string | null }) => p.proyecto + '|' + (p.identificacion || '');
  const proyFiltrados = useMemo(() => {
    const q = proyTexto.trim().toLowerCase();
    return (proyectos?.proyectos ?? []).filter((p) =>
      (!proySel || claveProy(p) === proySel) &&
      (!proyOcultarVacios || Number(p.movimientos) > 0) &&
      (!q || p.proyecto.toLowerCase().includes(q) || (p.identificacion || '').toLowerCase().includes(q)));
  }, [proyectos, proySel, proyTexto, proyOcultarVacios]);
  const proySort = useSort(proyFiltrados);
  const proyTot = proyFiltrados.reduce((a, p) => ({
    movimientos: a.movimientos + Number(p.movimientos), gastos: a.gastos + Number(p.gastos),
    ventas: a.ventas + Number(p.ventas), neto: a.neto + Number(p.neto),
  }), { movimientos: 0, gastos: 0, ventas: 0, neto: 0 });
  const hayFiltroProy = proySel !== '' || proyTexto.trim() !== '' || proyOcultarVacios;
  const anioProy = proyTodosAnios ? 0 : anio;

  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['sriDashboard', anio],
    queryFn: async () => {
      const response = await apiClient.post(
        '/rpc/sri_dashboard',
        { _anio: anio },
        { headers: FINANCIERO_HEADERS }
      );
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-300">
            No se pudo cargar el dashboard: {error instanceof Error ? error.message : 'error desconocido'}
          </p>
        </div>
      </div>
    );
  }

  const mensual = data.por_mes.map((m) => ({
    mes: MESES_CORTOS[m.mes - 1],
    [`${data.anio}`]: m.docs_actual,
    [`${data.anio - 1}`]: m.docs_anterior,
    [`importe ${data.anio}`]: m.importe_actual,
    [`importe ${data.anio - 1}`]: m.importe_anterior,
  }));

  const hayAnterior = data.resumen_anterior.documentos > 0;

  return (
    <div className={`p-6 space-y-6 min-h-full ${GLASS_BG}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Dashboard SRI</h1>
            <p className="text-sm text-muted-foreground">
              Comprobantes electrónicos · {data.anio}
              {hayAnterior && ` (comparado con ${data.anio - 1})`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="dash-anio" className="text-sm font-medium">Año</label>
          <select
            id="dash-anio"
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            value={anio}
            onChange={(e) => setAnio(Number(e.target.value))}
          >
            {(data.anios_disponibles.includes(anio)
              ? data.anios_disponibles
              : [anio, ...data.anios_disponibles]
            ).map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Pestañas Recibidos / Emitidos */}
      <div className="flex gap-1 border-b">
        {([['recibidos', 'Recibidos (compras)'], ['emitidos', 'Emitidos (ventas)'], ['proyectos', 'Por proyecto']] as const).map(([clave, etiqueta]) => (
          <button
            key={clave}
            onClick={() => setSeccion(clave)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              seccion === clave
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {seccion === 'recibidos' && (<>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard titulo="Documentos recibidos" valor={data.resumen.documentos}
          anterior={data.resumen_anterior.documentos} icono={FileText} />
        <KpiCard titulo="Importe total (neto de NC)" valor={data.resumen.importe_total}
          anterior={data.resumen_anterior.importe_total} icono={Wallet} formato={usd}
          desglose={desgloseImportes(data.por_tipo)} />
        <KpiCard titulo="IVA en compras" valor={data.resumen.iva}
          anterior={data.resumen_anterior.iva} icono={Percent} formato={usd} />
        <KpiCard titulo="Promedio por documento" valor={data.resumen.promedio}
          anterior={data.resumen_anterior.promedio} icono={BarChart3} formato={usd} />
        <KpiCard titulo="Proveedores únicos" valor={data.resumen.proveedores}
          anterior={data.resumen_anterior.proveedores} icono={Building2} />
      </div>

      <AvanceProcesamiento
        titulo={`Avance de procesamiento de recibidos ${data.anio}`}
        p={data.procesamiento}
        porTipo={data.procesamiento_por_tipo}
      />

      {/* Evolución mensual */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className={GLASS}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Documentos por mes</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={mensual}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mes" fontSize={12} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Legend />
                {hayAnterior && (
                  <Bar dataKey={`${data.anio - 1}`} fill="#94a3b8" radius={[3, 3, 0, 0]} />
                )}
                <Bar dataKey={`${data.anio}`} fill="#3b82f6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className={GLASS}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Importe por mes (USD)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={mensual}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="mes" fontSize={12} />
                <YAxis fontSize={12} tickFormatter={(v) => `$${Number(v).toLocaleString(LOCALE)}`} />
                <Tooltip formatter={(v) => usd(Number(v))} />
                <Legend />
                {hayAnterior && (
                  <Line type="monotone" dataKey={`importe ${data.anio - 1}`} stroke="#94a3b8"
                    strokeWidth={2} dot={{ r: 2 }} />
                )}
                <Line type="monotone" dataKey={`importe ${data.anio}`} stroke="#22c55e"
                  strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      </>)}

      {/* Emitidos (ventas) */}
      {seccion === 'emitidos' && (
        !data.emitidos || data.emitidos.resumen.documentos + data.emitidos.resumen_anterior.documentos === 0 ? (
          <Card className={GLASS}>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Sin documentos emitidos en {data.anio}. Descárguelos en Documentos Emitidos (SRI).
            </CardContent>
          </Card>
        ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard titulo="Documentos emitidos" valor={data.emitidos.resumen.documentos}
              anterior={data.emitidos.resumen_anterior.documentos} icono={FileText} />
            <KpiCard titulo="Importe facturado (neto de NC)" valor={data.emitidos.resumen.importe_total}
              anterior={data.emitidos.resumen_anterior.importe_total} icono={Wallet} formato={usd}
              desglose={desgloseImportes(data.emitidos.por_tipo)} />
            <KpiCard titulo="IVA en ventas" valor={data.emitidos.resumen.iva}
              anterior={data.emitidos.resumen_anterior.iva} icono={Percent} formato={usd} />
            <KpiCard titulo="Clientes únicos" valor={data.emitidos.resumen.receptores}
              anterior={data.emitidos.resumen_anterior.receptores} icono={Building2} />
          </div>

          <AvanceProcesamiento
            titulo={`Avance de procesamiento de emitidos ${data.anio}`}
            p={data.emitidos.procesamiento}
            porTipo={data.emitidos.procesamiento_por_tipo}
          />

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Distribución por tipo: documentos e importes */}
            <PorTipoCard porTipo={data.emitidos.por_tipo} anio={data.anio} />

            <Card className={GLASS}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Emitidos vs Recibidos por mes (USD)</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.por_mes.map((m, i) => ({
                    mes: MESES_CORTOS[m.mes - 1],
                    'Emitidos (ventas)': data.emitidos.por_mes[i]?.importe_actual ?? 0,
                    'Recibidos (compras)': m.importe_actual,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="mes" fontSize={12} />
                    <YAxis fontSize={12} tickFormatter={(v) => `$${Number(v).toLocaleString(LOCALE)}`} />
                    <Tooltip formatter={(v) => usd(Number(v))} />
                    <Legend />
                    <Line type="monotone" dataKey="Emitidos (ventas)" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="Recibidos (compras)" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className={GLASS}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Top 10 clientes por importe</CardTitle>
              </CardHeader>
              <CardContent>
                {data.emitidos.top_receptores.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-10 text-center">Sin documentos emitidos en {data.anio}</p>
                ) : (
                  <div className="space-y-2">
                    {data.emitidos.top_receptores.map((r, i) => {
                      const max = Number(data.emitidos.top_receptores[0].importe) || 1;
                      return (
                        <div key={r.ruc || i} className="text-sm">
                          <div className="flex justify-between gap-2">
                            <span className="truncate" title={`${r.ruc || 's/i'} · ${r.documentos} documentos`}>
                              {i + 1}. {r.razon_social || r.ruc || '(sin identificar)'}
                            </span>
                            <span className="font-medium whitespace-nowrap">{usd(Number(r.importe))}</span>
                          </div>
                          <div className="h-1.5 bg-muted rounded-full mt-1">
                            <div
                              className="h-1.5 bg-green-500 rounded-full"
                              style={{ width: `${Math.max(2, (Number(r.importe) / max) * 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
        )
      )}

      {seccion === 'recibidos' && (
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Distribución por tipo: documentos e importes */}
        <PorTipoCard porTipo={data.por_tipo} anio={data.anio} />

        {/* Top proveedores */}
        <Card className={GLASS}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top 10 proveedores por importe</CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_emisores.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center">Sin documentos en {data.anio}</p>
            ) : (
              <div className="space-y-2">
                {data.top_emisores.map((e, i) => {
                  const max = Number(data.top_emisores[0].importe) || 1;
                  return (
                    <div key={e.ruc} className="text-sm">
                      <div className="flex justify-between gap-2">
                        <span className="truncate" title={`${e.ruc} · ${e.documentos} documentos`}>
                          {i + 1}. {e.razon_social || e.ruc}
                        </span>
                        <span className="font-medium whitespace-nowrap">{usd(Number(e.importe))}</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full mt-1">
                        <div
                          className="h-1.5 bg-primary rounded-full"
                          style={{ width: `${Math.max(2, (Number(e.importe) / max) * 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Gastos / Ventas por proyecto (centro de costo) */}
      {seccion === 'proyectos' && (
        <Card className={GLASS}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Gastos y ventas por proyecto · {proyTodosAnios ? 'todos los años' : anio}</CardTitle>
            {proyectos && proyectos.proyectos.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 pt-2">
                <select value={proySel} onChange={(e) => setProySel(e.target.value)} aria-label="Proyecto"
                  className="h-9 max-w-xs rounded-md border bg-background px-2 text-sm">
                  <option value="">Todos los proyectos ({proyectos.proyectos.length})</option>
                  {[...proyectos.proyectos].sort((a, b) => a.proyecto.localeCompare(b.proyecto, 'es')).map((p) => (
                    <option key={claveProy(p)} value={claveProy(p)}>{p.proyecto}{p.identificacion ? ` · ${p.identificacion}` : ''}</option>
                  ))}
                </select>
                <input value={proyTexto} onChange={(e) => setProyTexto(e.target.value)} placeholder="Buscar proyecto o identificación…"
                  className="h-9 w-64 max-w-full rounded-md border bg-background px-2 text-sm" />
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={proyTodosAnios} onChange={(e) => setProyTodosAnios(e.target.checked)} /> Todos los años</label>
                <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={proyOcultarVacios} onChange={(e) => setProyOcultarVacios(e.target.checked)} /> Ocultar sin movimientos</label>
                {hayFiltroProy && (
                  <>
                    <span className="text-xs text-muted-foreground">{proyFiltrados.length} de {proyectos.proyectos.length}</span>
                    <button type="button" className="text-xs text-primary hover:underline" onClick={() => { setProySel(''); setProyTexto(''); setProyOcultarVacios(false); }}>Limpiar filtro</button>
                  </>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {!proyectos || proyectos.proyectos.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Sin proyectos {proyTodosAnios ? '' : `en ${anio}`}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3">N°</th>
                    <SortableTh col="proyecto" label="Proyecto" sort={proySort.sort} onSort={proySort.toggle} />
                    <SortableTh col="movimientos" label="Movimientos" align="right" sort={proySort.sort} onSort={proySort.toggle} />
                    <SortableTh col="gastos" label="Gastos (compras)" align="right" sort={proySort.sort} onSort={proySort.toggle} />
                    <SortableTh col="ventas" label="Ventas" align="right" sort={proySort.sort} onSort={proySort.toggle} />
                    <SortableTh col="neto" label="Neto (ventas − gastos)" align="right" sort={proySort.sort} onSort={proySort.toggle} className="pr-0" />
                  </tr>
                </thead>
                <tbody>
                  {proyFiltrados.length === 0 && (
                    <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Ningún proyecto coincide con el filtro.</td></tr>
                  )}
                  {proySort.sorted.map((p, i) => {
                    const abierto = proyAbierto === claveProy(p);
                    return (
                    <Fragment key={claveProy(p)}>
                    <tr onClick={() => setProyAbierto(abierto ? null : claveProy(p))} title="Clic para ver los documentos"
                      className={`cursor-pointer border-b last:border-0 ${abierto ? 'bg-primary/10' : 'hover:bg-muted/40'}`}>
                      <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3">
                        <p className="flex max-w-72 items-center gap-1 truncate">
                          {abierto ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                          {p.proyecto}</p>
                        {p.identificacion && <p className="text-xs text-muted-foreground">{p.identificacion}</p>}
                      </td>
                      <td className="py-2 pr-3 text-right">{p.movimientos}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap text-red-600 dark:text-red-400">
                        {p.gastos !== 0 ? usd(p.gastos) : '—'}</td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap text-green-600 dark:text-green-400">
                        {p.ventas !== 0 ? usd(p.ventas) : '—'}</td>
                      <td className={`py-2 text-right whitespace-nowrap font-medium ${
                        p.neto < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                        {usd(p.neto)}</td>
                    </tr>
                    {abierto && (
                      <tr className="border-b">
                        <td colSpan={6} className="bg-muted/30 p-2">
                          <ProyectoDetalle proyectoId={p.proyecto_id} anio={anioProy} nombre={p.proyecto} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t">
                    <td className="py-2 pr-3" colSpan={2}>Total{hayFiltroProy ? ' (filtrado)' : ''} · {proyFiltrados.length} proyecto(s)</td>
                    <td className="py-2 pr-3 text-right">{proyTot.movimientos}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(proyTot.gastos)}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">{usd(proyTot.ventas)}</td>
                    <td className="py-2 text-right whitespace-nowrap">{usd(proyTot.neto)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              El proyecto es el centro de costo asignado al procesar cada factura. Importes
              netos (las notas de crédito restan). "(Sin proyecto)" agrupa las facturas sin asignar.
              Salen todos los pacientes (proyectos) de la empresa, tengan o no consentimiento o movimientos.
              Haga clic en una fila para ver sus documentos y el RIDE.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
